import { useState } from 'react';

import { api } from '../api/client';
import { DEFAULT_TIMEFRAME_DAYS, TIMEFRAME_PRESETS } from '../timeframe';
import type { DashboardFilters, DashboardTile, SavedChart, SavedDashboard } from '../types';
import { ChartRenderer } from './ChartRenderer';
import { Empty } from '../charts/states';

/**
 * Assemble saved charts into a dashboard. The slug is what the platform stores
 * in the content item's `dashboardUrl` (`/d/<slug>`), so cutting a report over
 * is a one-field edit on the platform side — no deploy.
 */
interface Props {
  charts: SavedChart[];
  editing?: SavedDashboard | null;
  onSaved: (dashboard: SavedDashboard) => void;
  onCancel: () => void;
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const DashboardBuilder = ({ charts, editing, onSaved, onCancel }: Props) => {
  const [name, setName] = useState(editing?.name ?? '');
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(editing));
  const [description, setDescription] = useState(editing?.description ?? '');
  const [appFilter, setAppFilter] = useState<SavedDashboard['appFilter']>(
    editing?.appFilter ?? 'none',
  );
  const [tiles, setTiles] = useState<DashboardTile[]>(editing?.tiles ?? []);
  // A dashboard saved before filter settings existed has none, so it opens with
  // the platform/category filters on (what it did) and the time frame off. A new
  // one starts with the time frame on at 30 days.
  const [filters, setFilters] = useState<DashboardFilters>(
    editing?.filters ?? {
      platform: true,
      category: true,
      timeframe: { enabled: !editing, defaultDays: DEFAULT_TIMEFRAME_DAYS },
    },
  );
  // A dashboard saved before filter settings existed stays on its old rules
  // unless the author actually changes a filter here — saving an unrelated edit
  // must not quietly switch it to the new per-tile rules.
  const [filtersTouched, setFiltersTouched] = useState(false);
  const changeFilters = (next: DashboardFilters) => {
    setFiltersTouched(true);
    setFilters(next);
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const updateName = (value: string) => {
    setName(value);
    // Auto-derive the slug until the user edits it themselves.
    if (!slugTouched) setSlug(slugify(value));
  };

  const addTile = (chartId: string) =>
    setTiles((current) => [...current, { chartId, width: 6, height: 4 }]);

  const updateTile = (index: number, patch: Partial<DashboardTile>) =>
    setTiles((current) => current.map((t, i) => (i === index ? { ...t, ...patch } : t)));

  const removeTile = (index: number) =>
    setTiles((current) => current.filter((_, i) => i !== index));

  const moveTile = (index: number, delta: number) =>
    setTiles((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    if (!name.trim() || !slug.trim() || !description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api.saveDashboard({
          id: editing?.id,
          name: name.trim(),
          slug: slug.trim(),
          // Description is required (see the disabled state on the save
          // button) — it is the (i) content the ADO Chart Registry keys off.
          description: description.trim(),
          appFilter,
          filters: !editing || editing.filters || filtersTouched ? filters : undefined,
          // An empty override is dropped so the tile inherits its chart's text.
          tiles: tiles.map((t) => ({ ...t, description: t.description?.trim() || undefined })),
        }),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const canSave = Boolean(name.trim() && slug.trim() && description.trim());

  const chartById = (id: string) => charts.find((c) => c.id === id);

  return (
    <div className="studio">
      <div className="studio__panel">
        <h2>{editing ? 'Edit dashboard' : 'New dashboard'}</h2>

        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => updateName(e.target.value)} placeholder="App performance" />
        </label>

        <label>
          <span>Slug</span>
          <input
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            placeholder="app-performance"
          />
        </label>
        <p className="hint">
          The platform points a content item at <code>/d/{slug || 'your-slug'}</code>.
        </p>

        <label>
          <span>
            Description <span className="req" aria-hidden="true">*</span>
            <span className="visually-hidden"> (required)</span>
          </span>
          <input
            required
            aria-required="true"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this report shows and who it is for."
          />
        </label>
        <p className="hint">
          Required. Shown on the gallery card and copied into the{' '}
          <a href="/help#registry" target="_self">ADO Chart Registry</a> so the platform team can
          wire it up.
        </p>

        <label>
          <span>App filter</span>
          <select
            value={appFilter}
            onChange={(e) => setAppFilter(e.target.value as SavedDashboard['appFilter'])}
          >
            <option value="none">None — same data for everyone</option>
            <option value="single">Single app (app_id)</option>
            <option value="multiple">Multiple apps (app_ids)</option>
          </select>
        </label>

        <h3>Viewer filters</h3>
        <label className="check">
          <input
            type="checkbox"
            checked={filters.platform}
            onChange={(e) => changeFilters({ ...filters, platform: e.target.checked })}
          />
          <span>Store (Google Play / App Store)</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={filters.category}
            onChange={(e) => changeFilters({ ...filters, category: e.target.checked })}
          />
          <span>Category (follows the chosen store)</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={filters.timeframe.enabled}
            onChange={(e) =>
              changeFilters({ ...filters, timeframe: { ...filters.timeframe, enabled: e.target.checked } })
            }
          />
          <span>Time frame</span>
        </label>
        {filters.timeframe.enabled && (
          <label>
            <span>Default window</span>
            <select
              value={filters.timeframe.defaultDays}
              onChange={(e) =>
                changeFilters({
                  ...filters,
                  timeframe: { ...filters.timeframe, defaultDays: Number(e.target.value) },
                })
              }
            >
              {TIMEFRAME_PRESETS.map((p) => (
                <option key={p.days} value={p.days}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <p className="hint">
          Viewers can change the window or pick their own dates. A filter only reaches the charts
          whose query accepts it; the dashboard says how many that is.
        </p>

        <h3>Add a chart</h3>
        <div className="chart-picker">
          {charts.length === 0 && <p className="hint">Create a chart first.</p>}
          {charts.map((c) => (
            <button key={c.id} onClick={() => addTile(c.id)}>
              + {c.name}
            </button>
          ))}
        </div>

        <h3>Layout</h3>
        {tiles.length === 0 && <p className="hint">No tiles yet.</p>}
        <ol className="tile-list">
          {tiles.map((tile, i) => {
            const chart = chartById(tile.chartId);
            return (
              <li key={`${tile.chartId}-${i}`}>
                <span className="tile-list__name">{chart?.name ?? 'Missing chart'}</span>
                <label>
                  w
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={tile.width}
                    onChange={(e) => updateTile(i, { width: Number(e.target.value) })}
                  />
                </label>
                <label>
                  h
                  <input
                    type="number"
                    min={1}
                    value={tile.height}
                    onChange={(e) => updateTile(i, { height: Number(e.target.value) })}
                  />
                </label>
                <button onClick={() => moveTile(i, -1)} aria-label="Move up">↑</button>
                <button onClick={() => moveTile(i, 1)} aria-label="Move down">↓</button>
                <button onClick={() => removeTile(i)} aria-label="Remove">✕</button>
                <label className="tile-list__desc">
                  <span>(i) description</span>
                  <input
                    value={tile.description ?? ''}
                    onChange={(e) => updateTile(i, { description: e.target.value })}
                    placeholder={chart?.description || 'Inherits the chart description'}
                  />
                </label>
              </li>
            );
          })}
        </ol>

        {error && <p className="error">{error}</p>}

        <div className="studio__actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !canSave}>
            {editing ? 'Save changes' : 'Create dashboard'}
          </button>
        </div>
        {!editing && (
          <p className="hint reminder">
            After you create it, copy the new dashboard id, slug and description into the{' '}
            <a href="/help#registry" target="_self">ADO Chart Registry</a>. The platform's
            content items point at <code>/d/&lt;slug&gt;</code>, so the registry is what the team
            needs to know one exists.
          </p>
        )}
      </div>

      <div className="studio__preview">
        <header>Preview</header>
        <div className="dashboard__grid">
          {tiles.map((tile, i) => {
            const chart = chartById(tile.chartId);
            if (!chart) return null;
            return (
              <div
                className="dashboard__tile"
                key={`${tile.chartId}-${i}`}
                style={{
                  gridColumn: `span ${Math.min(12, Math.max(1, tile.width))}`,
                  minHeight: tile.height * 56,
                }}
              >
                <ChartRenderer
                  chart={chart}
                  title={tile.title ?? chart.name}
                  description={tile.description}
                />
              </div>
            );
          })}
          {tiles.length === 0 && <Empty message="Add a chart to see the layout" />}
        </div>
      </div>
    </div>
  );
};
